/**
 * @fileoverview Header — sticky nav + scroll blur + dil seçici + mobile menü.
 * Metinler ve nav: common.json; marka / linkler / CTA yolu: siteLayout (CMS ile birleşir).
 */

import { useState, useEffect } from 'react'
import { NavLink, Link }               from 'react-router-dom'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useTranslation }              from 'react-i18next'
import {
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineGlobeAlt,
  HiOutlineChevronDown,
} from 'react-icons/hi2'

import { useDispatch, useSelector }  from 'react-redux'
import { toggleMobileMenu, closeMobileMenu, selectMobileMenuOpen } from '../../features/ui/uiSlice'
import { useLanguage }               from '../../hooks/useLanguage'
import GoldButton                    from '../common/GoldButton'
import { cn }                        from '../../utils/cn'
import { DEFAULT_HEADER_NAV_LINKS }  from '../../features/siteLayout/defaultNavLinks'
import { parseNavLinkTo }            from '../../features/siteLayout/navTo'
import { normalizeSiteInternalPath }   from '../../config/siteRoutePathOptions'

/** Dil kodu → menü etiketi (CMS adı yoksa yedek) */
const LANG_LABELS = { tr: 'Türkçe', en: 'English', ru: 'Русский' }

function languageMenuLabel(code, name) {
  const n = String(name ?? '').trim()
  if (n) return n
  return LANG_LABELS[code] ?? String(code).toUpperCase()
}

function normalizeNavRows(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_HEADER_NAV_LINKS
  const out = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const to = String(row.to ?? '').trim()
    const labelKey = String(row.labelKey ?? '').trim()
    if (!to || !labelKey) continue
    const item = { to, labelKey }
    if (row.end) item.end = true
    out.push(item)
    if (out.length >= 20) break
  }
  return out.length ? out : DEFAULT_HEADER_NAV_LINKS
}

export default function Header() {
  const dispatch       = useDispatch()
  const mobileMenuOpen = useSelector(selectMobileMenuOpen)
  const { currentLanguage, changeLanguage, languageList } = useLanguage()
  const { t, i18n }    = useTranslation('common')
  const { t: ts }      = useTranslation('siteLayout')
  const [scrolled, setScrolled] = useState(false)

  const lang = (i18n.language || 'tr').split('-')[0]
  const bundle = i18n.getResourceBundle(lang, 'siteLayout')
  const navRows = normalizeNavRows(bundle?.header?.navLinks)
  const currentLangMeta = languageList.find((l) => l.code === currentLanguage)
  const currentLangLabel = languageMenuLabel(currentLanguage, currentLangMeta?.name)
  const currentLangCodeVisible = String(currentLanguage).toUpperCase()
  const brandName = ts('header.brandName', { defaultValue: 'GlobalAnka' })
  const ctaPath = normalizeSiteInternalPath(bundle?.header?.ctaPath, '/contact')

  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 72)
        ticking = false
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinkClass = ({ isActive }) =>
    cn(
      'text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-200 pb-0.5',
      isActive
        ? 'text-[#BF953F] border-b border-[#BF953F]/60'
        : 'text-white/60 hover:text-white'
    )

  return (
    <>
      <header
        className={cn(
          'fixed top-0 inset-x-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-[#0D172A]/90 backdrop-blur-md shadow-2xl shadow-black/30'
            : 'bg-transparent'
        )}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">

          <Link
            to="/"
            onClick={() => dispatch(closeMobileMenu())}
            className="text-xl md:text-2xl font-black tracking-tight leading-none flex-shrink-0"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              background: 'linear-gradient(135deg, #BF953F, #FCF6BA, #AA771C)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            aria-label={`${brandName} — ${t('nav.home')}`}
          >
            {brandName}
          </Link>

          <nav className="hidden md:flex items-center gap-8" aria-label={t('nav.home')}>
            {navRows.map(({ to, labelKey, end }) => (
              <NavLink key={`${to}-${labelKey}`} to={parseNavLinkTo(to)} end={Boolean(end)} className={navLinkClass}>
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">

            <Menu as="div" className="relative">
              <MenuButton
                className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors px-2 py-1.5 rounded"
                aria-label={`${t('nav.languageSelector')}: ${currentLangLabel} (${currentLangCodeVisible})`}
              >
                <HiOutlineGlobeAlt className="w-4 h-4" aria-hidden="true" />
                <span className="text-[11px] font-semibold uppercase tracking-widest">
                  {currentLangCodeVisible}
                </span>
                <HiOutlineChevronDown className="w-3 h-3" aria-hidden="true" />
              </MenuButton>

              <Transition
                enter="transition duration-150 ease-out"
                enterFrom="opacity-0 scale-95 -translate-y-1"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="transition duration-100 ease-in"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 -translate-y-1"
              >
                <MenuItems
                  className="absolute right-0 mt-2 w-36 origin-top-right bg-[#0D172A] border border-white/10 rounded-sm shadow-2xl shadow-black/40 focus:outline-none z-50 overflow-hidden"
                  aria-label={t('nav.languageOptions')}
                >
                  {languageList.map(({ code: langCode, name }) => (
                    <MenuItem key={langCode}>
                      {({ focus }) => (
                        <button
                          onClick={() => changeLanguage(langCode)}
                          className={cn(
                            'w-full text-left px-4 py-2.5 text-[11px] uppercase tracking-widest font-semibold transition-colors',
                            focus
                              ? 'bg-white/10 text-white'
                              : 'text-white/60 hover:text-white',
                            currentLanguage === langCode && 'text-[#BF953F]'
                          )}
                        >
                          {languageMenuLabel(langCode, name)}
                        </button>
                      )}
                    </MenuItem>
                  ))}
                </MenuItems>
              </Transition>
            </Menu>

            <GoldButton size="sm" href={ctaPath}>
              {t('nav.contact')}
            </GoldButton>
          </div>

          <button
            className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
            onClick={() => dispatch(toggleMobileMenu())}
            aria-label={mobileMenuOpen ? t('nav.menuClose') : t('nav.menuOpen')}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen
              ? <HiOutlineXMark className="w-6 h-6" aria-hidden="true" />
              : <HiOutlineBars3  className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        <Transition
          show={mobileMenuOpen}
          enter="transition-all duration-300 ease-out"
          enterFrom="opacity-0 -translate-y-3"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-200 ease-in"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-3"
        >
          <nav
            className="md:hidden bg-[#0D172A]/95 backdrop-blur-md border-t border-white/10 px-6 py-6 space-y-1"
            aria-label={t('nav.mobileNav')}
          >
            {navRows.map(({ to, labelKey, end }) => (
              <NavLink
                key={`m-${to}-${labelKey}`}
                to={parseNavLinkTo(to)}
                end={Boolean(end)}
                onClick={() => dispatch(closeMobileMenu())}
                className={({ isActive }) =>
                  cn(
                    'block py-3 px-4 text-sm font-semibold uppercase tracking-widest rounded transition-colors',
                    isActive
                      ? 'text-[#BF953F] bg-white/5'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  )
                }
              >
                {t(labelKey)}
              </NavLink>
            ))}

            <div className="flex gap-2 pt-4 px-4 border-t border-white/10">
              {languageList.map(({ code: langCode, name }) => (
                <button
                  key={langCode}
                  type="button"
                  onClick={() => { changeLanguage(langCode); dispatch(closeMobileMenu()) }}
                  aria-current={currentLanguage === langCode ? 'true' : undefined}
                  className={cn(
                    'text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-sm border transition-colors',
                    currentLanguage === langCode
                      ? 'border-[#BF953F]/60 text-[#BF953F]'
                      : 'border-white/20 text-white/50 hover:border-white/40 hover:text-white'
                  )}
                >
                  {languageMenuLabel(langCode, name)}
                </button>
              ))}
            </div>

            <div className="pt-4 px-4">
              <GoldButton size="sm" href={ctaPath} className="w-full justify-center">
                {t('cta.contactUs')}
              </GoldButton>
            </div>
          </nav>
        </Transition>
      </header>
    </>
  )
}
